import { useQuery } from '@tanstack/react-query'
import { Search, TriangleAlert, X } from 'lucide-react'
import { useEffect, useMemo, useRef, useState } from 'react'

import { stagesQuery, type StageRow } from '@/api/stages'
import { Button } from '@/components/ui/button'
import { Chip } from '@/components/ui/chip'
import { Input } from '@/components/ui/input'

import { BackgroundCard } from './BackgroundCard'
import { NO_WORLD, worldLabel } from './words'

/**
 * `/backgrounds` — every place a story can happen: search by name, narrow to one world, or show
 * only the ones that need a look (something marked wrong, or a check that fails). A card opens
 * the background's editor: its places (where to walk, the water, the sky…), its living scenery
 * and its details.
 */

export interface BackgroundsSearchState {
  q?: string
  world?: string
  needs_look?: boolean
}

export interface BackgroundsPageProps extends BackgroundsSearchState {
  onSearchChange?(next: BackgroundsSearchState): void
}

const GRID = 'grid grid-cols-[repeat(auto-fill,minmax(15rem,1fr))] gap-4'
/** Cards drawn at first; more follow as the list nears the bottom. */
export const PAGE = 48

export function BackgroundsPage({ q = '', world, needs_look, onSearchChange }: BackgroundsPageProps) {
  const [draft, setDraft] = useState(q)
  const current = useMemo<BackgroundsSearchState>(() => ({ q: q || undefined, world, needs_look: needs_look || undefined }), [q, world, needs_look])

  // The box is the source while typing; the URL follows 250 ms later (and wins when it changes).
  useEffect(() => setDraft(q), [q])
  useEffect(() => {
    if (draft === q) return
    const t = setTimeout(() => onSearchChange?.({ ...current, q: draft || undefined }), 250)
    return () => clearTimeout(t)
  }, [draft, q, current, onSearchChange])

  const set = (patch: Partial<BackgroundsSearchState>) => onSearchChange?.({ ...current, ...patch })

  const list = useQuery(stagesQuery({ q, world, needs_look }))
  // The world chips come from the whole catalog, so picking one never hides the others.
  const all = useQuery(stagesQuery({}))
  const worlds = useMemo(() => {
    const seen = new Map<string, number>()
    for (const w of all.data?.worlds ?? []) seen.set(w.id, w.count)
    if (seen.size === 0) for (const r of all.data?.items ?? []) seen.set(r.world || NO_WORLD, (seen.get(r.world || NO_WORLD) ?? 0) + 1)
    if (world && !seen.has(world)) seen.set(world, 0)
    return [...seen.entries()].sort((a, b) => worldLabel(a[0]).localeCompare(worldLabel(b[0])))
  }, [all.data, world])

  // The server filters; the world is re-checked here so a lenient server still shows the right cards
  // (the server counts a stage with no world under `other`).
  const items = (list.data?.items ?? []).filter((r) => !world || (r.world || NO_WORLD) === world)

  return (
    <div className="mx-auto flex w-full max-w-7xl flex-col gap-6 px-4 py-8" data-testid="backgrounds-page">
      <header className="flex flex-wrap items-end justify-between gap-4">
        <div className="flex max-w-2xl flex-col gap-1">
          <h1 className="text-3xl font-semibold tracking-tight text-ink">Backgrounds</h1>
          <p className="text-ink-2">The places stories happen in. Show the studio where to walk, where the water and the sky are, and what lives in the background.</p>
        </div>
      </header>

      <div className="flex flex-col gap-3">
        <div className="flex flex-wrap items-center gap-3">
          <div className="relative min-w-56 flex-1 sm:max-w-sm">
            <Search className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-ink-3" aria-hidden />
            <Input value={draft} onChange={(e) => setDraft(e.target.value)} placeholder="Find a place: beach, bedroom, forest…" aria-label="Search backgrounds" className="pl-9 pr-9" />
            {draft ? (
              <button type="button" onClick={() => setDraft('')} aria-label="Clear search" className="absolute right-2 top-1/2 inline-flex size-7 -translate-y-1/2 items-center justify-center rounded-lg text-ink-3 hover:bg-paper-2 hover:text-ink">
                <X className="size-4" aria-hidden />
              </button>
            ) : null}
          </div>
          <Chip selected={Boolean(needs_look)} onClick={() => set({ needs_look: needs_look ? undefined : true })} icon={<TriangleAlert className="size-4 text-warn" aria-hidden />} data-testid="needs-look">
            Needs a look
          </Chip>
        </div>
        <div className="flex flex-wrap items-center gap-1.5" role="group" aria-label="World">
          <Chip size="sm" selected={!world} onClick={() => set({ world: undefined })}>
            All worlds
          </Chip>
          {worlds.map(([id, count]) => (
            <Chip key={id} size="sm" selected={world === id} onClick={() => set({ world: world === id ? undefined : id })} data-testid={`world-${id}`}>
              {worldLabel(id)}
              {count ? <span className="ml-1 text-ink-3">{count}</span> : null}
            </Chip>
          ))}
        </div>
      </div>

      <Results rows={items} pending={list.isPending} error={list.isError} onRetry={() => void list.refetch()} q={q} filtered={Boolean(world || needs_look)} />
    </div>
  )
}

function Results({ rows, pending, error, onRetry, q, filtered }: { rows: StageRow[]; pending: boolean; error: boolean; onRetry(): void; q: string; filtered: boolean }) {
  const [shown, setShown] = useState(PAGE)
  // A new search starts from the top.
  const key = rows.length === 0 ? '' : `${rows[0]!.uid}|${rows.length}`
  useEffect(() => setShown(PAGE), [key])

  if (pending) {
    return (
      <ul className={GRID} aria-busy="true" aria-label="Loading backgrounds">
        {Array.from({ length: 8 }, (_, i) => (
          <li key={i} className="card overflow-hidden">
            <div className="aspect-video animate-pulse-soft bg-paper-2" />
            <div className="flex flex-col gap-2 p-3">
              <div className="h-4 w-2/3 rounded bg-paper-2" />
              <div className="h-6 w-full rounded-full bg-paper-2" />
            </div>
          </li>
        ))}
      </ul>
    )
  }
  if (error) {
    return (
      <div className="card flex flex-col items-start gap-3 p-6" role="alert">
        <p className="text-ink">The backgrounds could not be loaded.</p>
        <Button variant="secondary" onClick={onRetry}>
          Try again
        </Button>
      </div>
    )
  }
  if (rows.length === 0) {
    return (
      <p className="card p-8 text-center text-ink-2" data-testid="backgrounds-empty">
        {q ? `No backgrounds match “${q}”.` : filtered ? 'Nothing here — every background in this view looks fine.' : 'No backgrounds yet. Refresh the picture library in Settings to bring them in.'}
      </p>
    )
  }
  return (
    <>
      <p className="-mt-2 text-sm text-ink-3" aria-live="polite">
        {rows.length === 1 ? '1 background' : `${rows.length} backgrounds`}
      </p>
      <ul className={GRID} role="list" aria-label="Backgrounds" data-testid="backgrounds-grid">
        {rows.slice(0, shown).map((r) => (
          <BackgroundCard key={r.uid} row={r} />
        ))}
      </ul>
      <ShowMore hasMore={shown < rows.length} onMore={() => setShown((n) => n + PAGE)} />
    </>
  )
}

/** A sentinel that shows the next cards as it nears the viewport, plus a button. */
function ShowMore({ hasMore, onMore }: { hasMore: boolean; onMore(): void }) {
  const ref = useRef<HTMLDivElement | null>(null)
  const more = useRef(onMore)
  more.current = onMore
  useEffect(() => {
    const el = ref.current
    if (!el || !hasMore || typeof IntersectionObserver === 'undefined') return
    const io = new IntersectionObserver((entries) => entries.some((e) => e.isIntersecting) && more.current(), { rootMargin: '600px 0px' })
    io.observe(el)
    return () => io.disconnect()
  }, [hasMore])
  if (!hasMore) return null
  return (
    <div ref={ref} className="flex justify-center py-6">
      <Button variant="secondary" onClick={onMore}>
        Show more
      </Button>
    </div>
  )
}
